import { DateTime, Duration } from 'luxon'
import {
  ActivatedOffer,
  BatchPlaytimeConfig,
  getMinutesInBatch,
  PlaytimeBatchInfo,
  GenericTask,
  TaskType,
} from '@playback-rewards/shared-libraries'

//constants
import { CLAIM_SPECIAL_REWARD_REASON } from 'constants/index'
//types
import { SpecialOfferDetails } from 'types'

export const getActiveIndex = (
  batches: number[],
  playtimeBatchInfo: PlaytimeBatchInfo | undefined
) => {
  if (!playtimeBatchInfo?.activatedOffer?.isInstallAttributed) {
    return -1
  }
  return batches.findIndex((_, index) => {
    const batchStartTime = playtimeBatchInfo?.getBatchStartTime(index) || DateTime.now().toJSDate()
    const batchEndTime = playtimeBatchInfo?.getBatchEndTime(index) || DateTime.now().toJSDate()
    const luxonStartTime = DateTime.fromJSDate(batchStartTime)
    const luxonEndTime = DateTime.fromJSDate(batchEndTime)
    const luxonCurrentTime = DateTime.now()
    return luxonCurrentTime > luxonStartTime && luxonCurrentTime < luxonEndTime
  })
}

export const getTimeDifference = (endTime: DateTime, startTime: DateTime) => {
  return endTime.diff(startTime, ['hours', 'minutes', 'seconds'])
}

export const getTimeValue = (time: Duration | undefined) => {
  if (!time) {
    return ''
  } else if (time?.hours > 0) {
    return `${Math.round(time.hours)}hr`
  } else if (time.minutes > 0) {
    return `${Math.round(time.minutes)}min`
  } else if (time.seconds > 0) {
    return `${Math.round(time.seconds)}sec`
  }
  return ''
}

export const checkIfGenericPlaytimeMMPOffer = (offer: SpecialOfferDetails | ActivatedOffer) => {
  return (
    Boolean(offer?.batchPlaytimeConfig) &&
    offer?.tasks?.some((task) => task.taskType === CLAIM_SPECIAL_REWARD_REASON.MMP_EVENT)
  )
}

export const checkIfPlaytimeTaskCompleted = (
  batchPlaytimeConfig: BatchPlaytimeConfig | undefined,
  playtimeBatchInfo: PlaytimeBatchInfo | undefined,
  index: number
) => {
  if (!batchPlaytimeConfig || !playtimeBatchInfo || index < 0) {
    return true
  }
  const minutesInBatch = getMinutesInBatch(batchPlaytimeConfig) || 0
  const remainingMinutes = Math.ceil(playtimeBatchInfo?.getRemainingMinutesInBatch(index) || 0)
  const progress = Math.round(((minutesInBatch - remainingMinutes) / minutesInBatch) * 100)
  return progress >= 100
}

export const isLockedGenericTask = (genericTask: GenericTask, allGenericTasks: GenericTask[]) => {
  return (
    genericTask.previousTaskRequirements
      .map((requirement) =>
        allGenericTasks.find((task) => task.taskId === requirement && !task.completed)
      )
      .filter((event) => event !== undefined)?.length > 0
  )
}

export const isExpiredGenericTask = (genericTask: GenericTask) => {
  return genericTask.endTimestamp
    ? transformTimestampToDate(genericTask.endTimestamp) < new Date()
    : false
}

export const isGenericTaskNotStarted = (genericTask: GenericTask) => {
  return genericTask.startTimestamp
    ? transformTimestampToDate(genericTask.startTimestamp) >= new Date()
    : false
}

export const isHiddenNotStartedGenericTask = (genericTask: GenericTask, isInstalled: boolean) => {
  return (
    genericTask.hideBeforeActive &&
    (genericTask.startTimestamp ? isGenericTaskNotStarted(genericTask) : !isInstalled)
  )
}

export const isGenericTaskHidden = (genericTask: GenericTask, isInstalled: boolean) => {
  return (
    isHiddenNotStartedGenericTask(genericTask, isInstalled) || isExpiredGenericTask(genericTask)
  )
}

export const isPurchaseTaskLocked = (genericTask: GenericTask, hasParsingEnabled: boolean) => {
  return (
    [TaskType.PURCHASE, TaskType.PURCHASE_AMOUNT].includes(genericTask.taskType) &&
    !hasParsingEnabled
  )
}

export const transformTimestampToDate = (timestamp: any) => {
  const millis = timestamp._seconds * 1000 + timestamp._nanoseconds / 1000000
  return new Date(millis)
}

export const formatTaskTimeLeft = (time: Duration | undefined) => {
  if (!time) {
    return ''
  }
  if (time.hours >= 36) {
    return `${Math.round(time.as('days'))} days left`
  }
  return time.toFormat('hh:mm:ss')
}
